import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MagnifyingGlass, Users, Package, CheckSquare, Square } from "@phosphor-icons/react";

/**
 * Bulk Adjust Quota Panel — Manager-only.
 * Pick multiple technicians × multiple parts, set a single `max_qty`, submit once.
 * Empty part selection means "all master parts" (matches backend contract).
 */
export default function BulkQuotaAdjustDialog({ open, onClose, onSaved }) {
  const [engineers, setEngineers] = useState([]);
  const [parts, setParts] = useState([]);
  const [engIds, setEngIds] = useState(new Set());
  const [partCodes, setPartCodes] = useState(new Set());
  const [maxQty, setMaxQty] = useState(20);
  const [engFilter, setEngFilter] = useState("");
  const [partFilter, setPartFilter] = useState("");
  const [applyAllParts, setApplyAllParts] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setEngIds(new Set()); setPartCodes(new Set());
    setEngFilter(""); setPartFilter(""); setMaxQty(20); setApplyAllParts(false);
    setLoading(true);
    Promise.all([
      api.get("/engineers"),
      api.get("/stock?limit=5000"),
    ])
      .then(([e, s]) => { setEngineers(e.data || []); setParts(s.data || []); })
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, [open]);

  const filteredEng = useMemo(() => {
    const q = engFilter.trim().toLowerCase();
    if (!q) return engineers;
    return engineers.filter((e) =>
      (e.name || "").toLowerCase().includes(q) || (e.email || "").toLowerCase().includes(q)
    );
  }, [engineers, engFilter]);

  const filteredParts = useMemo(() => {
    const q = partFilter.trim().toLowerCase();
    if (!q) return parts.slice(0, 200); // keep the list snappy
    return parts.filter((p) =>
      (p.item || "").toLowerCase().includes(q) || (p.description || "").toLowerCase().includes(q)
    ).slice(0, 200);
  }, [parts, partFilter]);

  const toggle = (set, val) => {
    const next = new Set(set);
    if (next.has(val)) next.delete(val); else next.add(val);
    return next;
  };

  const submit = async () => {
    if (engIds.size === 0) return toast.error("Pick at least one technician");
    if (!applyAllParts && partCodes.size === 0) return toast.error("Pick at least one part (or turn on 'Apply to all master parts')");
    const q = parseInt(maxQty, 10);
    if (!q || q <= 0) return toast.error("Cap must be > 0");
    setBusy(true);
    const t = toast.loading("Applying bulk quota adjustment…");
    try {
      const body = {
        engineer_ids: Array.from(engIds),
        stock_codes: applyAllParts ? null : Array.from(partCodes),
        max_qty: q,
      };
      const res = await api.post("/engineers/part-quotas/bulk-adjust", body);
      toast.dismiss(t);
      toast.success(
        `Done: ${res.data.engineers} tech × ${res.data.parts} part${res.data.parts === 1 ? "" : "s"}. ` +
        `Created ${res.data.created}, updated ${res.data.updated}. Cap = ${res.data.max_qty}.`
      );
      onSaved && onSaved();
      onClose();
    } catch (e) { toast.dismiss(t); toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl rounded-none border-neutral-900 max-h-[90vh] overflow-y-auto" data-testid="bulk-quota-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">Bulk Adjust Quotas</DialogTitle>
        </DialogHeader>

        <div className="border border-neutral-200 bg-neutral-50 px-3 py-2 text-[11px] text-neutral-600">
          Pick a group of technicians (e.g. senior techs), pick a set of parts (e.g. high-turnover consumables), and set the cap once.
          Leave the part list empty and turn on <b>Apply to all master parts</b> to bump every part across the selected techs.
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Technicians picker */}
          <div className="border border-neutral-300 bg-white">
            <div className="border-b border-neutral-200 px-3 py-2 bg-neutral-50 flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider">
                <Users size={13} weight="bold" />
                Technicians ({engIds.size} selected)
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setEngIds(new Set(filteredEng.map((e) => e.id)))}
                  className="text-[10px] font-mono uppercase tracking-wider hover:text-neutral-900 text-neutral-600"
                  data-testid="bulk-eng-all"
                >All</button>
                <button
                  type="button"
                  onClick={() => setEngIds(new Set())}
                  className="text-[10px] font-mono uppercase tracking-wider hover:text-neutral-900 text-neutral-600"
                  data-testid="bulk-eng-none"
                >Clear</button>
              </div>
            </div>
            <div className="px-3 py-2 border-b border-neutral-200">
              <div className="relative">
                <MagnifyingGlass size={13} weight="bold" className="absolute left-2 top-1/2 -translate-y-1/2 text-neutral-400" />
                <Input
                  placeholder="Filter…"
                  value={engFilter}
                  onChange={(e) => setEngFilter(e.target.value)}
                  className="pl-7 h-8 rounded-none border-neutral-300 text-xs"
                  data-testid="bulk-eng-filter"
                />
              </div>
            </div>
            <div className="max-h-72 overflow-y-auto divide-y divide-neutral-100">
              {loading && <div className="p-4 text-xs text-neutral-400 text-center">Loading…</div>}
              {!loading && filteredEng.length === 0 && (
                <div className="p-4 text-xs text-neutral-400 text-center">No matches.</div>
              )}
              {!loading && filteredEng.map((e) => {
                const on = engIds.has(e.id);
                return (
                  <label
                    key={e.id}
                    className={`flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-neutral-50 text-sm ${on ? "bg-neutral-50" : ""}`}
                    data-testid={`bulk-eng-${e.id}`}
                  >
                    {on
                      ? <CheckSquare size={14} weight="fill" className="text-neutral-900" />
                      : <Square size={14} weight="regular" className="text-neutral-400" />}
                    <input type="checkbox" checked={on} onChange={() => setEngIds(toggle(engIds, e.id))} className="sr-only" />
                    <span className="flex-1 truncate">{e.name}</span>
                    <span className="text-[10px] font-mono text-neutral-400">{(e.region || "").slice(0, 12)}</span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Parts picker */}
          <div className={`border bg-white ${applyAllParts ? "border-neutral-300 opacity-50 pointer-events-none" : "border-neutral-300"}`}>
            <div className="border-b border-neutral-200 px-3 py-2 bg-neutral-50 flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider">
                <Package size={13} weight="bold" />
                Parts ({applyAllParts ? "ALL master parts" : `${partCodes.size} selected`})
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setPartCodes(new Set(filteredParts.map((p) => p.item)))}
                  className="text-[10px] font-mono uppercase tracking-wider hover:text-neutral-900 text-neutral-600"
                  data-testid="bulk-part-all"
                  disabled={applyAllParts}
                >Filtered</button>
                <button
                  type="button"
                  onClick={() => setPartCodes(new Set())}
                  className="text-[10px] font-mono uppercase tracking-wider hover:text-neutral-900 text-neutral-600"
                  data-testid="bulk-part-none"
                  disabled={applyAllParts}
                >Clear</button>
              </div>
            </div>
            <div className="px-3 py-2 border-b border-neutral-200">
              <div className="relative">
                <MagnifyingGlass size={13} weight="bold" className="absolute left-2 top-1/2 -translate-y-1/2 text-neutral-400" />
                <Input
                  placeholder="Filter parts by code or description…"
                  value={partFilter}
                  onChange={(e) => setPartFilter(e.target.value)}
                  className="pl-7 h-8 rounded-none border-neutral-300 text-xs"
                  data-testid="bulk-part-filter"
                />
              </div>
              <div className="text-[10px] text-neutral-500 mt-1 font-mono">
                Showing first 200 matches. Refine the filter to narrow it down.
              </div>
            </div>
            <div className="max-h-72 overflow-y-auto divide-y divide-neutral-100">
              {loading && <div className="p-4 text-xs text-neutral-400 text-center">Loading…</div>}
              {!loading && filteredParts.length === 0 && (
                <div className="p-4 text-xs text-neutral-400 text-center">No matches.</div>
              )}
              {!loading && filteredParts.map((p) => {
                const on = partCodes.has(p.item);
                return (
                  <label
                    key={p.item}
                    className={`flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-neutral-50 text-sm ${on ? "bg-neutral-50" : ""}`}
                    data-testid={`bulk-part-${p.item}`}
                  >
                    {on
                      ? <CheckSquare size={14} weight="fill" className="text-neutral-900" />
                      : <Square size={14} weight="regular" className="text-neutral-400" />}
                    <input type="checkbox" checked={on} onChange={() => setPartCodes(toggle(partCodes, p.item))} className="sr-only" />
                    <span className="flex-1">
                      <div className="text-xs font-mono">{p.item}</div>
                      {p.description && <div className="text-[10px] text-neutral-500 truncate">{p.description}</div>}
                    </span>
                    <span className="text-[10px] font-mono text-neutral-400">on-hand {p.on_hand_qty}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>

        <label className="flex items-center gap-2 border border-neutral-300 px-3 py-2 cursor-pointer select-none hover:border-neutral-900">
          <input
            type="checkbox"
            checked={applyAllParts}
            onChange={(e) => setApplyAllParts(e.target.checked)}
            className="h-4 w-4 accent-neutral-900"
            data-testid="bulk-all-parts-toggle"
          />
          <span className="text-sm">Apply cap to <b>every part on the master list</b> for the selected technicians</span>
        </label>

        <div className="grid grid-cols-2 gap-3 items-end">
          <div className="space-y-1.5">
            <Label className="label-eyebrow">New cap (max_qty)</Label>
            <Input
              type="number" min={1}
              value={maxQty}
              onChange={(e) => setMaxQty(e.target.value)}
              className="rounded-none border-neutral-400 font-mono h-10 text-right"
              data-testid="bulk-max-qty"
            />
          </div>
          <div className="text-xs text-neutral-500">
            <div>Techs: <b>{engIds.size}</b></div>
            <div>Parts: <b>{applyAllParts ? "ALL master parts" : partCodes.size}</b></div>
            <div>Total rows to touch: <b>{engIds.size * (applyAllParts ? (parts.length || 0) : partCodes.size)}</b></div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none border-neutral-900 hover:bg-neutral-900 hover:text-white">Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy || engIds.size === 0 || (!applyAllParts && partCodes.size === 0)}
            data-testid="bulk-submit"
            className="rounded-none bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-40"
          >
            {busy ? "Applying…" : "Apply cap"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
